'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { useUser } from '@/features/auth/model/auth.selectors';
import { ANONYMOUS_SCOPE } from '@/shared/lib/user-scoped-storage';
import {
  subscribeActiveProject,
  getActiveProjectSnapshot,
  setActiveProject,
  clearActiveProject,
} from '@/shared/lib/active-project-store';

/**
 * Persist and retrieve the last-opened building.
 *
 * Backed by the in-memory {@link active-project-store} (NOT a raw localStorage
 * round-trip): some browsers (Brave with ephemeral/locked storage) drop
 * localStorage writes silently, which previously caused an infinite persist loop
 * and reset the active building to `projects[0]` on every secondary page. The
 * store keeps an authoritative in-memory pointer for the session and mirrors to
 * localStorage best-effort (scoped per `${userId}:` so logout wipes it in one
 * pass — see `wipeUserScopedKeys`).
 */
export function useLastProject() {
  const userId = useUser()?.id ?? ANONYMOUS_SCOPE;

  const lastProjectId = useSyncExternalStore(
    subscribeActiveProject,
    () => getActiveProjectSnapshot(userId),
    () => null
  );

  const updateLastProject = useCallback(
    (projectId: string) => {
      setActiveProject(userId, projectId);
    },
    [userId]
  );

  const clearLastProject = useCallback(() => {
    clearActiveProject(userId);
  }, [userId]);

  return {
    lastProjectId,
    updateLastProject,
    clearLastProject,
  };
}
